/**
 * Source-of-truth for "how many rows render in the My Playlists tab" so
 * the tab badge count and the actual rendered row count cannot diverge.
 *
 * `ProfileMyPlaylists` renders four kinds of system rows above/below the
 * custom playlist list:
 *
 * | Row                | Render condition                                         |
 * | ------------------ | -------------------------------------------------------- |
 * | Currently Watching | `inProgressItems.length > 0` (any viewer)                |
 * | Watch Later        | `!isPreview` (owner-only, always shown for the owner)    |
 * | Favorites          | `!isPreview` (owner-only, always shown for the owner)    |
 * | Watch History      | `!isPreview` (owner-only, always shown for the owner)    |
 *
 * The three owner-only rows are listed in `OWNER_ONLY_SYSTEM_ROWS`; the
 * count is derived from `length` rather than hardcoded. `Currently Watching`
 * is intentionally not in the list because its render gate depends on
 * data (in-progress items), not viewer identity.
 *
 * If a row is added or removed in `ProfileMyPlaylists`, update the list
 * here in the same change so the badge stays in sync.
 */
export const OWNER_ONLY_SYSTEM_ROWS = [
  'watch-later',
  'favorites',
  'watch-history',
] as const

export interface MyPlaylistsRowCountInput {
  /** Number of user-authored ("custom") playlists rendered in the tab. */
  customPlaylistsCount: number
  /** True when the Currently Watching row will render (any in-progress items). */
  hasInProgressItems: boolean
  /** True when viewing someone else's profile — the three owner-only rows are hidden. */
  isPreview: boolean
}

/**
 * Total number of rows that will render in the My Playlists tab —
 * custom playlists + Currently Watching (when applicable) + the three
 * owner-only system rows (when not in preview).
 */
export function computeMyPlaylistsRowCount ({
  customPlaylistsCount,
  hasInProgressItems,
  isPreview,
}: MyPlaylistsRowCountInput): number {
  const currentlyWatching = hasInProgressItems ? 1 : 0
  const ownerSystemRows = isPreview ? 0 : OWNER_ONLY_SYSTEM_ROWS.length
  return customPlaylistsCount + currentlyWatching + ownerSystemRows
}
